<template>
    <ConfigSection :title="$t('phu_production.configuration.holding')" required>
        <div v-for="workstation in workstations" :key="workstation.id" class="holding-config">
            <h3 class="holding-config__workstation">{{ workstation.name }}</h3>
            <draggable
                v-if="groups[workstation.id]"
                tag="transition-group"
                :list="groups[workstation.id]"
                :disabled="true"
                :animation="280"
                easing="cubic-bezier(0.25, 0.1, 0.25, 1)"
                ghost-class="holding-config__ghost"
                :no-transition-on-drag="true"
                :component-data="listComponentData"
                @change="syncOrder(workstation.id)"
            >
                <PhuConfigCard
                    v-for="(phu, index) in groups[workstation.id]"
                    :key="phu.id"
                    :phu="phu"
                    :products="products"
                    :under-assigned="underAssigned(workstation.id)"
                    :min-slots="minSlots"
                    :layouts="layouts"
                    :is-first="index === 0"
                    :is-last="index === groupOf(workstation.id).length - 1"
                    @rename="$emit('rename', { phuId: phu.id, name: $event })"
                    @move="$emit('move', { phuId: phu.id, direction: $event })"
                    @remove="$emit('remove', phu)"
                    @select-layout="$emit('select-layout', { phuId: phu.id, layout: $event })"
                    @edit-layout="$emit('edit-layout', phu.id)"
                    @select-slot="$emit('select-slot', { phu, slot: $event })"
                    @clear-slot="$emit('clear-slot', { phuId: phu.id, slotId: $event.id })"
                />
            </draggable>
            <button type="button" class="holding-config__add" @click="$emit('add', workstation.id)">
                <i class="i-andy-add i-andy-s" aria-hidden="true"></i>
                {{ $t('phu_production.configuration.add_phu') }}
            </button>
        </div>
    </ConfigSection>
</template>

<script>
import draggable from 'vuedraggable'
import ConfigSection from './ConfigSection.vue'
import PhuConfigCard from './PhuConfigCard.vue'
import { getUnderAssignedProducts } from '../utils/validation'

export default {
    name: 'HoldingSection',
    components: { ConfigSection, PhuConfigCard, draggable },
    props: {
        workstations: { type: Array, required: true },
        phus: { type: Array, required: true },
        products: { type: Object, required: true },
        layouts: { type: Array, default: () => [] },
        minSlots: { type: Number, required: true }
    },
    data() {
        return {
            groups: {}
        }
    },
    computed: {
        listComponentData() {
            return {
                props: { name: 'phu-reorder', tag: 'div' },
                attrs: { class: 'holding-config__list' }
            }
        }
    },
    watch: {
        phus: {
            immediate: true,
            deep: true,
            handler() {
                this.syncLists()
            }
        },
        workstations() {
            this.syncLists()
        }
    },
    methods: {
        phusOf(workstationId) {
            return this.phus.filter((phu) => phu.workstation_id === workstationId).sort((a, b) => a.sort_order - b.sort_order)
        },
        groupOf(workstationId) {
            return this.groups[workstationId] || []
        },
        underAssigned(workstationId) {
            return getUnderAssignedProducts(this.phusOf(workstationId), this.minSlots)
        },
        sameOrder(current, next) {
            return current.length === next.length && current.every((phu, index) => phu === next[index])
        },
        syncLists() {
            if (!this.workstations) {
                return
            }
            this.workstations.forEach((workstation) => {
                const next = this.phusOf(workstation.id)
                const current = this.groups[workstation.id] || []
                if (!this.sameOrder(current, next)) {
                    this.$set(this.groups, workstation.id, next)
                }
            })
        },
        syncOrder(workstationId) {
            this.groupOf(workstationId).forEach((phu, index) => {
                phu.sort_order = index + 1
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.holding-config {
    @include display-flex();
    @include flex-direction(column);
    gap: 8px;

    &__workstation {
        margin: 0;
        font-family: $text-bold;
        font-size: 1.125rem;
        color: $phu-text;
    }

    ::v-deep > div,
    ::v-deep .holding-config__list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
    }

    ::v-deep .holding-config__ghost {
        opacity: 0.45;
    }

    ::v-deep .phu-reorder-move {
        transition: transform 280ms ease;
    }

    &__add {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        gap: 8px;
        width: 100%;
        min-height: 36px;
        border: 1px dashed $phu-primary;
        border-radius: $phu-radius-s;
        background-color: $phu-button;
        font-family: $text-bold;
        font-size: 1rem;
        color: $phu-primary;
        cursor: pointer;
    }
}
</style>
